import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Autocomplete,
  Box
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

function InventoryForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    medicineId: '',
    totalStock: 0,
    minStockLevel: '',
    unitPrice: '',
    unitsPerPackage: '',
    location: '',
    notes: ''
  });

  const [medicines, setMedicines] = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchMedicines();
    if (isEdit) {
      fetchInventory();
    }
  }, [id]);

  const fetchMedicines = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/medicines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedicines(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (err) {
      console.error('Error fetching medicines:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load medicines');
      setMedicines([]);
    }
  };

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/inventory/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const inventoryData = response.data;
      setFormData({
        medicineId: inventoryData.medicineId,
        totalStock: inventoryData.totalStock,
        minStockLevel: inventoryData.minStockLevel || '',
        unitPrice: inventoryData.unitPrice || '',
        unitsPerPackage: inventoryData.unitsPerPackage || '',
        location: inventoryData.location || '',
        notes: inventoryData.notes || ''
      });

      // Set selected medicine for autocomplete
      if (inventoryData.medicine) {
        setSelectedMedicine(inventoryData.medicine);
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load inventory data');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMedicineChange = (event, value) => {
    setSelectedMedicine(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        medicineId: value.id
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        medicineId: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        minStockLevel: formData.minStockLevel ? parseInt(formData.minStockLevel) : null,
        unitPrice: formData.unitPrice ? parseFloat(formData.unitPrice) : null,
        unitsPerPackage: formData.unitsPerPackage ? parseInt(formData.unitsPerPackage) : null
      };

      if (isEdit) {
        await axios.put(`${API_URL}/inventory/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Inventory updated successfully!');
      } else {
        await axios.post(`${API_URL}/inventory`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Inventory created successfully!');
      }

      setTimeout(() => {
        navigate('/inventory');
      }, 1500);
    } catch (err) {
      console.error('Error saving inventory:', err);
      setError(err.response?.data?.error || 'Failed to save inventory');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEdit ? 'Edit Inventory' : 'Add New Inventory'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Medicine Selection */}
            <Grid item xs={12}>
              <Autocomplete
                value={selectedMedicine}
                onChange={handleMedicineChange}
                options={medicines}
                getOptionLabel={(option) => option?.name ? `${option.name} - ${option.category || ''}` : ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Medicine *"
                    required={!isEdit}
                    helperText={`${medicines.length} medicine(s) available`}
                  />
                )}
                disabled={isEdit}
                fullWidth
                noOptionsText="No medicines found. Please add medicines first."
              />
              {isEdit && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Medicine cannot be changed in edit mode
                </Typography>
              )}
              {!isEdit && medicines.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No medicines available. Please add medicines from the Medicines page before creating inventory.
                </Alert>
              )}
            </Grid>

            {/* Total Stock */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Stock"
                name="totalStock"
                type="number"
                value={formData.totalStock}
                onChange={handleChange}
                required
                disabled={isEdit}
                helperText={isEdit ? "Update stock via batch management" : "Initial stock quantity"}
              />
            </Grid>

            {/* Minimum Stock Level */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Stock Level"
                name="minStockLevel"
                type="number"
                value={formData.minStockLevel}
                onChange={handleChange}
                helperText="Alert threshold for low stock"
              />
            </Grid>

            {/* Unit Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Price (per strip/package)"
                name="unitPrice"
                type="number"
                value={formData.unitPrice}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: 0 }}
                helperText="Price per strip/package (₹)"
              />
            </Grid>

            {/* Units Per Package */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Units Per Package"
                name="unitsPerPackage"
                type="number"
                value={formData.unitsPerPackage}
                onChange={handleChange}
                inputProps={{ min: 1, step: 1 }}
                helperText="Tablets/ml per strip or pack — enables loose selling. Leave blank if sold whole only."
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Storage Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                helperText="e.g., Shelf A1, Cabinet B2"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Additional information about this inventory item"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/inventory')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEdit ? 'Update Inventory' : 'Create Inventory'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default InventoryForm;
